'use client'

import React, { useEffect, useState, type JSX } from 'react'
import Link from 'next/link'
import classNames from 'classnames'
import ConditionalWrapper from '../utils/conditional-wrapper'
import { useComponentVisible } from '../hooks'
import Icon from '../icon'

export type DropdownItemType = {
  title: string | JSX.Element
  value?: string
  href?: string
  subNavigationItems?: []
}

type Props = {
  activeItem?: string | JSX.Element
  buttonText?: (arg0: string | JSX.Element) => JSX.Element
  items: DropdownItemType[]
  onItemClick?: (arg0: string) => void
  links?: boolean
  clientSideNavigation?: boolean
  className?: string
  buttonClassName?: string
  style?: React.CSSProperties
  big?: boolean
  noBorderEffect?: boolean
  stripped?: boolean
  staticTitle?: string
  subNavigation?: boolean
}

export const Dropdown = ({
  activeItem = '',
  buttonText,
  items,
  onItemClick,
  links = false,
  clientSideNavigation = links,
  className = '',
  buttonClassName = '',
  style,
  big = false,
  noBorderEffect = false,
  stripped = false,
  staticTitle = '',
  subNavigation = false,
}: Props) => {
  const { componentRef, isVisible, setIsVisible } = useComponentVisible<HTMLDivElement>(false)
  const [overflowAuto, setOverflowAuto] = useState(false)
  const ItemType = links ? 'a' : 'button'

  const [isContentVisible, setIsContentVisible] = useState(isVisible)

  const activeItemTitle = items.find(i => (i.value ?? i.title) === activeItem)?.title || ''

  useEffect(() => {
    setIsVisible(false)
  }, [activeItem])

  useEffect(() => {
    let timeoutOverflow: NodeJS.Timeout

    if (isVisible) {
      timeoutOverflow = setTimeout(() => setOverflowAuto(true), 300)
      setIsContentVisible(true)
    } else {
      setOverflowAuto(false)
      setIsContentVisible(false)
    }

    return () => {
      clearTimeout(timeoutOverflow)
    }
  }, [isVisible])

  return (
    <div
      className={classNames('c-dropdown', className, {
        'c-dropdown--active': isVisible,
        'c-dropdown--stripped': stripped,
        'c-dropdown--no-border-effect': noBorderEffect || stripped,
        'c-dropdown--big': big && !stripped,
      })}
      style={style}>
      <button
        className={classNames('u-outline-inset c-dropdown__btn', buttonClassName, {
          'c-dropdown__btn--active': isContentVisible,
        })}
        onClick={() => setIsVisible(!isVisible)}
        aria-expanded={isVisible}>
        <span className="c-dropdown__title u-overflow-ellipsis">
          {buttonText
            ? buttonText(activeItemTitle)
            : staticTitle.length
            ? staticTitle
            : activeItemTitle}
        </span>
        <Icon
          name="arrow-down-small"
          className={classNames('c-dropdown__icon', {
            'i-32': big,
            'i-20': !big,
          })}
        />
      </button>
      <div
        ref={componentRef}
        className={`c-dropdown__content-wrapper ${
          overflowAuto ? 'u-overflow-auto' : 'u-overflow-hidden'
        }`}>
        <div className={classNames('c-dropdown__content', { 'd-none': !isContentVisible })}>
          <ul className="ul-stripped u-mb-0 u-fs-16">
            {items.map((item, i) => {
              const itemId = item.value ?? item.title.toString()
              const itemIsActive = itemId === activeItem

              return (
                <li key={i} className="c-dropdown__container">
                  <ConditionalWrapper
                    condition={links && clientSideNavigation}
                    wrapper={children => (
                      <Link prefetch={false} href={item.href ?? ''} legacyBehavior>
                        {children}
                      </Link>
                    )}>
                    <ItemType
                      {...(links && !clientSideNavigation && { href: item.href })}
                      className={classNames('c-btn c-btn--block c-dropdown__item', {
                        'c-dropdown__item--active': itemIsActive,
                      })}
                      onClick={() => (!links && onItemClick ? onItemClick(itemId) : null)}>
                      {item.title}
                    </ItemType>
                  </ConditionalWrapper>
                  <div>
                    {subNavigation &&
                      item.subNavigationItems &&
                      item.subNavigationItems?.length > 0 &&
                      item.subNavigationItems?.map((item: any, key: number) => (
                        <a
                          key={item.url + key}
                          href={item.url}
                          className="d-block u-fs-14 u-pb-12 u-pl-32 u-pr-16 c-dropdown__item__subitem--item u-color-black">
                          {item.displayName}
                        </a>
                      ))}
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </div>
  )
}

export default Dropdown
